import { datadogLogs } from '@datadog/browser-logs';
import { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { useBandwidthContext } from '../BandwidthContext';
import { QueryQueueContext } from './QueryQueueContext';
import { Query, QueryQueue, QueryQueueContextType } from './types';

const ACTIVE_INTERVAL_MS = 500;

export const QueryQueueProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [queryQueue, setQueryQueue] = useState<QueryQueue>([]);
  const [queryAdded, setQueryAdded] = useState(false);
  const { status } = useBandwidthContext();
  const runningQueriesRef = useRef<Record<string, number>>({});

  const setRunningQuery = useCallback((id: string, running: boolean) => {
    setQueryQueue((prev) => {
      const index = prev.findIndex((query) => query.id === id);
      if (index !== -1) {
        const copy = [...prev];
        const copyQuery = copy[index];
        if (!running && copyQuery.running) {
          copyQuery.count = (copyQuery.count || 0) + 1;
        }
        copyQuery.running = running;
        copy[index] = copyQuery;

        return copy;
      }

      return prev;
    });

    if (running) {
      runningQueriesRef.current[id] = Date.now();
    } else {
      delete runningQueriesRef.current[id];
    }
  }, []);

  const value: QueryQueueContextType = {
    queryQueue,
    addQuery: (query: Query) => {
      setQueryQueue((prev) => {
        const exists = prev.some((existingQuery) => {
          return existingQuery.id === query.id;
        });
        if (exists) {
          datadogLogs.logger.warn('QUEUE_CONFLICT: Duplicate query already in custom queue', {
            queryId: query.id,
            existingQueueLength: prev.length,
            timestamp: Date.now(),
          });
          return prev;
        }

        datadogLogs.logger.debug('QUEUE_DEBUG: Adding query to custom queue', {
          queryId: query.id,
          queueLength: prev.length + 1,
          networkStatus: status,
          timestamp: Date.now(),
        });

        setQueryAdded(true);
        return [...prev, query];
      });
    },
    setRunningQuery,
    removeQuery: (id: string) => {
      setQueryQueue((prev) => prev.filter((query) => query.id !== id));
      delete runningQueriesRef.current[id];
    },
    getPendingFareStatus: (id: string) => {
      // @ts-expect-error - TS doesn't know that queryQueue is an array of Query objects
      const queryWithStatus = queryQueue.findLast((query) => {
        const parsedQuery = JSON.parse(query.id);
        return parsedQuery?.status && parsedQuery.fareId === id;
      });
      return queryWithStatus ? JSON.parse(queryWithStatus.id).status : '';
    },
    hasQueriesPending: (id: string) => {
      return queryQueue.some((query) => {
        const parsedQuery = JSON.parse(query.id);
        return parsedQuery.fareId === id && !!query.count;
      });
    },
    isAcknowledgeQueryPending: (id: string) => {
      return queryQueue.some((query) => {
        const parsedQuery = JSON.parse(query.id);
        return parsedQuery.fareId === id && !parsedQuery.status && !parsedQuery.incident;
      });
    },
    getPendingRequestAction: (requestId: string) => {
      const pendingQuery = queryQueue.find((query) => {
        try {
          const parsedQuery = JSON.parse(query.id);
          return parsedQuery.requestId === requestId;
        } catch {
          return false;
        }
      });
      if (pendingQuery) {
        const parsedQuery = JSON.parse(pendingQuery.id);
        return parsedQuery.action as 'accept' | 'reject';
      }
      return null;
    },
    queryAdded,
    setQueryAdded,
  };

  const unstackQueue = useCallback(
    async (queue: QueryQueue) => {
      if (queue.length === 0) return;
      const nextQuery = queue.find((query) => !query.running);
      if (!nextQuery) return;

      const { queryFn, id } = nextQuery;

      datadogLogs.logger.debug('QUEUE_DEBUG: Executing query from custom queue', {
        queryId: id,
        queueLength: queue.length,
        runningQueries: Object.keys(runningQueriesRef.current).length,
        networkStatus: status,
        timestamp: Date.now(),
      });

      try {
        setRunningQuery(id, true);
        const startTime = Date.now();
        await queryFn();
        const duration = Date.now() - startTime;

        datadogLogs.logger.debug('QUEUE_DEBUG: Query execution successful', {
          queryId: id,
          duration,
          timestamp: Date.now(),
        });
      } catch (err) {
        datadogLogs.logger.error('QUEUE_DEBUG: Query execution failed', {
          queryId: id,
          error: err,
          errorMessage: err instanceof Error ? err.message : 'Unknown error',
          timestamp: Date.now(),
        });
        setRunningQuery(id, false);
      }
    },
    [setRunningQuery, status]
  );

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (status !== 'offline') {
      intervalId = setInterval(() => {
        unstackQueue(queryQueue);
      }, ACTIVE_INTERVAL_MS);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [queryQueue, status, unstackQueue]);

  return <QueryQueueContext.Provider value={value}>{children}</QueryQueueContext.Provider>;
};
